import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Search, Locate, Bookmark, Check, ChevronDown } from "lucide-react";
import { TIDS } from "@/constants/testIds";
import { searchCity, reverseGeocode } from "@/lib/geocoding";
import { useLocation } from "@/contexts/LocationContext";
import { useAuth } from "@/contexts/AuthContext";
import { QUICK_LOCATIONS } from "@/lib/quickLocations";
import api from "@/lib/api";
import { toast } from "sonner";

export default function LocationPicker({ compact = false }) {
  const { location, setLocation } = useLocation();
  const { user, refresh, setAuthOpen } = useAuth();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [saving, setSaving] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (query.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      const r = await searchCity(query);
      setResults(r);
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const pick = (r) => {
    setLocation({
      name: r.name,
      country: r.country,
      lat: r.lat,
      lng: r.lng,
      timezone: r.timezone,
      displayName: r.displayName || (r.country ? `${r.name}, ${r.country}` : r.name),
    });
    setOpen(false);
    setQuery("");
    setResults([]);
    toast.success(`Location · ${r.displayName || r.name}`);
  };

  const geolocate = () => {
    if (!navigator.geolocation) { toast.error("Geolocation not supported"); return; }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const loc = await reverseGeocode(pos.coords.latitude, pos.coords.longitude);
        setLocation(loc);
        toast.success("Using your current location");
        setOpen(false);
      },
      () => toast.error("Could not get your location")
    );
  };

  const saveToAccount = async () => {
    if (!user) {
      // Stash this location so it auto-saves after sign-in / sign-up.
      try {
        localStorage.setItem("thaigayu_pending_save_location", JSON.stringify({
          name: location.name, lat: location.lat, lng: location.lng,
          country: location.country || "", timezone: location.timezone,
          displayName: location.displayName,
        }));
      } catch { /* ignore quota errors */ }
      setAuthOpen(true);
      toast.message(`Sign up to save ${location.displayName}. We'll keep it for you.`);
      return;
    }
    try {
      setSaving(true);
      await api.post("/me/locations", {
        name: location.name, lat: location.lat, lng: location.lng,
        country: location.country || "", timezone: location.timezone,
      });
      await refresh();
      toast.success(`Saved ${location.displayName}`);
    } catch {
      toast.error("Could not save location");
    } finally { setSaving(false); }
  };

  const alreadySaved = user?.saved_locations?.some(
    (s) => s.name === location.name && Math.abs(s.lat - location.lat) < 0.01
  );

  return (
    <div className="relative" ref={wrapperRef}>
      <div className="flex flex-col gap-3">
        {/* On mobile we stack the trigger and the save button vertically and
            make the trigger full-width so it reads as an obvious tappable
            field. On sm+ we keep the inline pill layout. */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid={TIDS.locCurrent}
            aria-label={`Change location, current ${location.displayName}`}
            className={`group flex items-center gap-2 sm:gap-3 rounded-full sm:rounded-full border-2 hover:bg-[rgba(216,185,122,0.10)] transition-colors duration-300 ${
              compact
                ? "w-full sm:w-auto justify-between sm:justify-start px-4 py-3 sm:py-2 border-[var(--emerald)]/45 hover:border-[var(--emerald)]"
                : "px-5 py-3 border-[var(--cream)]/15 hover:border-[var(--emerald)]"
            }`}
            type="button"
          >
            <span className="flex items-center gap-2 sm:gap-3 min-w-0">
              <MapPin size={15} className="text-[var(--emerald)] shrink-0" />
              <span className={`tracking-wide text-[var(--cream)] truncate ${compact ? "text-sm font-medium" : "text-sm"}`}>
                {location.displayName}
              </span>
            </span>
            <span className="flex items-center gap-2 shrink-0">
              {compact && (
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--emerald-soft)] opacity-90 group-hover:opacity-100 transition-opacity">
                  change
                </span>
              )}
              <ChevronDown
                size={15}
                className={`text-[var(--emerald-soft)] transition-transform duration-300 ${open ? "rotate-180" : ""}`}
              />
            </span>
          </button>
          <button
            onClick={saveToAccount}
            data-testid={TIDS.locSaveBtn}
            disabled={saving || alreadySaved}
            aria-label={alreadySaved ? "Location already saved" : "Save this location to your account"}
            className={`flex items-center justify-center gap-2 rounded-full border border-[var(--cream)]/12 uppercase tracking-[0.18em] text-[var(--cream)]/70 hover:text-[var(--emerald-soft)] hover:border-[var(--emerald)] transition-colors duration-500 disabled:opacity-40 ${
              compact ? "w-full sm:w-auto px-4 py-2.5 sm:py-2 text-[10px]" : "px-4 py-3 text-xs"
            }`}
          >
            {alreadySaved ? <Check size={compact ? 12 : 14} /> : <Bookmark size={compact ? 12 : 14} />}
            <span>{alreadySaved ? "Saved" : "Save location"}</span>
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3, ease: [0.7, 0, 0.2, 1] }}
            className="absolute z-40 mt-3 w-[min(440px,calc(100vw-32px))] left-0 sm:left-auto glass rounded-2xl overflow-hidden"
          >
            <div className="flex items-center gap-3 px-4 py-3 border-b border-[var(--cream)]/8">
              <Search size={14} className="text-[var(--cream)]/50" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search any city, town or village…"
                data-testid={TIDS.locSearchInput}
                className="flex-1 bg-transparent outline-none text-sm text-[var(--cream)] placeholder:text-[var(--cream)]/35"
              />
              <button
                onClick={geolocate}
                data-testid={TIDS.locGeolocateBtn}
                className="text-[10px] uppercase tracking-[0.18em] text-[var(--cream)]/55 hover:text-[var(--emerald-soft)] flex items-center gap-1"
              >
                <Locate size={12} /> use mine
              </button>
            </div>
            <div className="max-h-72 overflow-auto" data-testid={TIDS.locResultsList}>
              {query.length < 2 && (
                <div className="px-4 py-3">
                  <div className="eyebrow mb-2">popular</div>
                  {QUICK_LOCATIONS.map((q) => (
                    <button
                      key={q.name}
                      onClick={() => pick(q)}
                      className="w-full text-left py-2 hover:text-[var(--emerald-soft)] flex items-center justify-between transition-colors"
                    >
                      <span className="text-sm">{q.displayName}</span>
                      <span className="font-mono text-[10px] text-[var(--cream)]/35">{q.timezone}</span>
                    </button>
                  ))}
                </div>
              )}
              {results.length === 0 && query.length >= 2 && (
                <div className="px-4 py-6 text-sm text-[var(--cream)]/50">No results.</div>
              )}
              {results.map((r, i) => (
                <button
                  key={r.id}
                  onClick={() => pick(r)}
                  data-testid={TIDS.locResultItem(i)}
                  className="w-full text-left px-4 py-3 hover:bg-[var(--cream)]/4 transition-colors"
                >
                  <div className="text-sm text-[var(--cream)]">{r.name}</div>
                  <div className="text-[11px] text-[var(--cream)]/45">{[r.admin1, r.country].filter(Boolean).join(", ")}</div>
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
